import React, { useEffect, useState } from "react";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import Select from "react-select";
import {
  createGeneralSettings,
  updateGeneralSettings,
} from "../../api/SettingsApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { IGeneralSettings } from "../../exports/interfaces";
import { useLoader } from "../../context/LoaderContext";
import { Upload } from "lucide-react";
import { usePermission } from "../../hooks/usePermission";
import {
  renderExistingFilePreview,
  renderFilePreview,
  validateFileSize,
} from "../../exports/functions";
import { isValidEmail } from "../register/registerValidation";

const SelectWithError = Select as unknown as React.ComponentType<any>;

interface GeneralSettingProps {
  generalSettings: IGeneralSettings | null;
  setGeneralSettings: React.Dispatch<
    React.SetStateAction<IGeneralSettings | null>
  >;
}

const GeneralSetting = ({
  generalSettings,
  setGeneralSettings,
}: GeneralSettingProps) => {
  const { setLoading } = useLoader();
  const { canEdit } = usePermission();
  const [generalData, setGeneralData] = useState<
    IGeneralSettings & { file: File | null }
  >({
    websiteName: "",
    file: null,
    fileName: "",
    address: "",
    phone: "",
    email: "",
    timeZone: "",
    dateFormat: "",
  });
  const [errors, setErrors] = useState({
    websiteName: "",
    address: "",
    phone: "",
    email: "",
    timeZone: "",
    dateFormat: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    const updated = {
      ...generalData,
      [name]: value,
    } as typeof generalData;

    setGeneralData(updated);
    // setGeneralSettings((prev) => ({
    //   ...(prev ?? {}),
    //   [name]: value,
    // } as IGeneralSettings));
    // setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const getGeneralErrors = (data: typeof generalData) => {
    const validationErrors = {
      websiteName: "",
      address: "",
      phone: "",
      email: "",
      timeZone: "",
      dateFormat: "",
    };

    if (!data.websiteName?.trim()) {
      validationErrors.websiteName = "Website Name is required.";
    }

    if (!data.address?.trim()) {
      validationErrors.address = "Address is required.";
    }

    if (!data.phone?.trim()) {
      validationErrors.phone = "Phone Number is required.";
    } else if (!/^\d{10}$/.test(data.phone.trim())) {
      validationErrors.phone = "Exactly 10 digits required.";
    }

    if (!data.email?.trim()) {
      validationErrors.email = "Email is required.";
    } else if (!isValidEmail(data.email)) {
      validationErrors.email = "Invalid Email format.";
    }

    if (!data.timeZone?.trim()) {
      validationErrors.timeZone = "Time Zone is required.";
    }

    if (!data.dateFormat?.trim()) {
      validationErrors.dateFormat = "Date Format is required.";
    }

    return validationErrors;
  };

  const scrollToFirstError = (validationErrors: Record<string, string>) => {
    const fieldOrder = [
      "websiteName",
      "address",
      "phone",
      "email",
      "timeZone",
      "dateFormat",
    ];

    const firstErrorField = fieldOrder.find((field) => validationErrors[field]);

    if (!firstErrorField) return;

    const element =
      firstErrorField === "timeZone" || firstErrorField === "dateFormat"
        ? (document.querySelector(
            `[data-field="${firstErrorField}"]`,
          ) as HTMLElement | null)
        : (document.querySelector(
            `[name="${firstErrorField}"]`,
          ) as HTMLElement | null);

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      // element.focus?.();
    }
  };

  const validateGeneralFields = (data: typeof generalData) => {
    const validationErrors = getGeneralErrors(data);
    setErrors(validationErrors);

    if (Object.values(validationErrors).some((error) => error !== "")) {
      scrollToFirstError(validationErrors);
      return false;
    }

    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!validateFileSize(file)) {
        e.target.value = "";
        return;
      }
      const updated = {
        ...generalData,
        fileName: file.name,
        file: file,
      };

      setGeneralData(updated);
      // setGeneralSettings((prev) => ({
      //   ...(prev ?? {}),
      //   fileName: file.name,
      // } as IGeneralSettings));
    }
  };

  const timeZoneOptions = [
    { value: "Asia/Kathmandu", label: "Asia/Kathmandu" },
    {
      value: "UTC (Coordinated Universal Time)",
      label: "UTC (Coordinated Universal Time)",
    },
    { value: "GMT (Greenwich Mean Time)", label: "GMT (Greenwich Mean Time)" },
    {
      value: "Daylight Saving Time (DST)",
      label: "Daylight Saving Time (DST)",
    },
  ];

  const dateFormatOptions = [
    { value: "MM / DD / YYYY", label: "MM / DD / YYYY" },
    { value: "DD / MM / YYYY", label: "DD / MM / YYYY" },
    { value: "YYYY / MM / DD", label: "YYYY / MM / DD" },
  ];

  const handleSelectChange = (
    name: keyof any,
    selected: SelectOption | null,
  ) => {
    const updated = {
      ...generalData,
      [name]: selected?.value,
    } as typeof generalData;

    setGeneralData(updated);
    // setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    if (!validateGeneralFields(generalData)) {
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();

      payload.append("websiteName", generalData.websiteName);
      payload.append("address", generalData.address);
      payload.append("phone", generalData.phone);
      payload.append("email", generalData.email);
      payload.append("timeZone", generalData.timeZone);
      payload.append("dateFormat", generalData.dateFormat);

      if (generalData.file) {
        payload.append("documentUrl", generalData.file);
      }

      const response = generalSettings?.id
        ? await updateGeneralSettings(generalSettings.id, payload)
        : await createGeneralSettings(payload);

      if (response.status === 200 || response.status === 201) {
        const savedData = response.data;
        setGeneralSettings(savedData);
        setGeneralData((prev) => ({
          ...prev,
          ...savedData,
          file: prev.file,
        }));
        dynamicSuccess(
          generalSettings
            ? "General settings updated successfully!"
            : "General settings saved successfully!",
        );
      }
    } catch (error: any) {
      dynamicFailed(`${error.message}`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (generalSettings) {
      setGeneralData((prev) => ({
        ...prev,
        ...generalSettings,
      }));
    }
  }, [generalSettings]);

  return (
    <>
      <div className={`dashboard-card`} style={{ borderTopLeftRadius: 0 }}>
        <div className="row g-3">
          <div className="col-md-6">
            <h5 className="mb-1">Website Identity</h5>
            <small>Brand the application for your municipality.</small>

            <div className="mt-2 row g-3">
              {/* Website Name */}
              <div className="col-md-6">
                <label className="form-label">Website Name</label>
                <input
                  type="text"
                  className={`form-control ${errors.websiteName ? "input-error" : ""}`}
                  placeholder="Enter Website Name"
                  name="websiteName"
                  value={generalData.websiteName}
                  onChange={handleChange}
                />
                {errors.websiteName && (
                  <p className="error-message">{errors.websiteName}</p>
                )}
                <small className="setting-description">
                  Displayed in the sidebar and browser tab
                </small>
              </div>

              {/* Address */}
              <div className="col-md-6">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  className={`form-control ${errors.address ? "input-error" : ""}`}
                  placeholder="Enter Address"
                  name="address"
                  value={generalData.address}
                  onChange={handleChange}
                />
                {errors.address && (
                  <p className="error-message">{errors.address}</p>
                )}
              </div>

              {/* Logo Upload */}
              <div className="col-12">
                <label className="form-label">Municipality Logo</label>

                <div
                  className={`upload-box p-4 ${!generalData?.file && !generalData?.documentUrl && "text-center"}`}
                >
                  {generalData?.file ? (
                    renderFilePreview(
                      generalData.file,
                      generalData.fileName,
                      (e: any) => handleFileChange(e),
                    )
                  ) : generalData?.documentUrl ? (
                    renderExistingFilePreview(
                      generalData.documentUrl,
                      handleFileChange,
                    )
                  ) : (
                    <>
                      <Upload />

                      <p className="mb-1">
                        Choose a file or drag and drop it here.
                      </p>

                      <small className="d-block mb-3">
                        (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                      </small>

                      <label className="btn btn-badge text-main-color">
                        Browse File
                        <input
                          hidden
                          type="file"
                          onChange={(e) => {
                            handleFileChange(e);
                          }}
                        />
                      </label>
                    </>
                  )}
                </div>
              </div>

              {/* Phone + Email */}
              <div className="col-md-6">
                <label className="form-label">Phone</label>
                <input
                  type="text"
                  className={`form-control ${errors.phone ? "input-error" : ""}`}
                  placeholder="Enter Phone"
                  name="phone"
                  value={generalData.phone}
                  onChange={(e) => {
                    if (/^[0-9]*$/.test(e.target.value)) {
                      handleChange(e);
                    }
                  }}
                  pattern="\d*"
                  maxLength={10}
                />
                {errors.phone && (
                  <p className="error-message">{errors.phone}</p>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className={`form-control ${errors.email ? "input-error" : ""}`}
                  placeholder="Enter Email"
                  name="email"
                  value={generalData.email}
                  onChange={handleChange}
                />
                {errors.email && (
                  <p className="error-message">{errors.email}</p>
                )}
              </div>

              {/* Time Zone + Date Format */}
              <div className="col-md-6" data-field="timeZone">
                <label className="form-label">Time Zone</label>
                <SelectWithError
                  styles={customSelectStyles}
                  isError={Boolean(errors.timeZone)}
                  options={timeZoneOptions}
                  placeholder="Select time zone"
                  value={
                    timeZoneOptions.find(
                      (option) => option.value === generalData.timeZone,
                    ) || null
                  }
                  onChange={(selected: SelectOption | null) =>
                    handleSelectChange("timeZone", selected)
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {errors.timeZone && (
                  <p className="error-message">{errors.timeZone}</p>
                )}
              </div>

              <div className="col-md-6" data-field="dateFormat">
                <label className="form-label">Date Format</label>
                <SelectWithError
                  styles={customSelectStyles}
                  isError={Boolean(errors.dateFormat)}
                  options={dateFormatOptions}
                  placeholder="Select Date Format"
                  value={
                    dateFormatOptions.find(
                      (option) => option.value === generalData.dateFormat,
                    ) || null
                  }
                  onChange={(selected: SelectOption | null) =>
                    handleSelectChange("dateFormat", selected)
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {errors.dateFormat && (
                  <p className="error-message">{errors.dateFormat}</p>
                )}
              </div>

              {/* Submit */}
              <div className="col-12 d-flex justify-content-start mt-4">
                <button
                  className="btn btn-badge-primary px-3"
                  onClick={handleSubmit}
                  disabled={!canEdit("Settings")}
                  style={{
                    pointerEvents: canEdit("Settings") ? "auto" : "none",
                    opacity: canEdit("Settings") ? 1 : 0.5,
                  }}
                >
                  Save Change
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralSetting;
